// src/utils/enhancedReceiptGenerator.js
import { ReceiptGenerator } from './receiptGenerator'
import { HTMLReceiptGenerator } from './htmlReceiptGenerator'
import ProductService from '../services/productService'

export class EnhancedReceiptGenerator {
  static productCache = new Map()

  /**
   * Get product details by ID with caching
   * @param {string} productId - Product ID
   * @returns {Promise<Object>} Product details
   */
  static async getProductDetails(productId) {
    if (!productId) return null

    // Check cache first
    if (this.productCache.has(productId)) {
      return this.productCache.get(productId)
    }

    try {
      const productService = new ProductService()
      const response = await productService.getProductById(productId)
      
      if (response?.data) {
        this.productCache.set(productId, response.data)
        return response.data
      }
    } catch (error) {
      console.warn('Failed to fetch product details:', error)
    }

    return null
  }

  /**
   * Enhance sale items with product details
   * @param {Array} items - Original sale items
   * @returns {Promise<Array>} Enhanced items with product details
   */
  static async enhanceItemsWithProductDetails(items) {
    if (!Array.isArray(items)) return []

    const enhancedItems = await Promise.all(
      items.map(async (item, index) => {
        // Try to get product details if we have product ID
        const productId = item.productId || item.product_id || item.id || item.product?._id || item.product?.id
        let productDetails = null

        if (productId) {
          productDetails = await this.getProductDetails(productId)
        }

        // If no API call needed/successful but we have embedded product data, use it
        if (!productDetails && item.product) {
          productDetails = item.product
        }

        // Build enhanced item with fallback chain
        const enhancedItem = {
          ...item,
          name: this.getItemName(item, productDetails, index),
          description: this.getItemDescription(item, productDetails),
          category: this.getItemCategory(item, productDetails),
          sku: this.getItemSKU(item, productDetails)
        }

        return enhancedItem
      })
    )

    return enhancedItems
  }

  /**
   * Get item name with enhanced fallback logic
   * @param {Object} item - Original item
   * @param {Object} productDetails - Product details from API
   * @param {number} index - Item index
   * @returns {string} Item name
   */
  static getItemName(item, productDetails, index) {
    // Priority order for item name
    const possibleNames = [
      // From API call
      productDetails?.name,
      productDetails?.productName,
      productDetails?.title,
      // From embedded product data (common in order responses)
      item.product?.name,
      item.product?.productName,
      item.product?.title,
      // From item directly
      item.name,
      item.productName,
      item.product_name,
      item.itemName,
      item.title,
      item.description,
      `Item #${index + 1}`
    ]

    return possibleNames.find(name => name && typeof name === 'string' && name.trim()) || `Item #${index + 1}`
  }

  /**
   * Get item description
   * @param {Object} item - Original item
   * @param {Object} productDetails - Product details from API
   * @returns {string} Item description
   */
  static getItemDescription(item, productDetails) {
    return productDetails?.description || 
           item.product?.description ||
           item.description || 
           item.notes || 
           ''
  }

  /**
   * Get item category
   * @param {Object} item - Original item
   * @param {Object} productDetails - Product details from API
   * @returns {string} Item category
   */
  static getItemCategory(item, productDetails) {
    return productDetails?.category?.name || 
           productDetails?.categoryName ||
           item.product?.category?.name ||
           item.product?.categoryName ||
           item.category ||
           item.categoryName ||
           ''
  }

  /**
   * Get item SKU
   * @param {Object} item - Original item
   * @param {Object} productDetails - Product details from API
   * @returns {string} Item SKU
   */
  static getItemSKU(item, productDetails) {
    return productDetails?.sku || 
           item.product?.sku ||
           item.sku || 
           item.productSKU ||
           ''
  }

  /**
   * Generate enhanced PDF receipt with product details
   * @param {Object} sale - Sale data
   * @param {string} filename - PDF filename
   * @param {Object} options - Receipt options
   */
  static async generateEnhancedPDFReceipt(sale, filename, options = {}) {
    try {
      // Show loading indicator if possible
      console.log('🔍 Fetching product details for receipt...')

      // Enhance items with product details
      const enhancedSale = {
        ...sale,
        items: await this.enhanceItemsWithProductDetails(sale.items || [])
      }

      // Generate PDF with enhanced data
      ReceiptGenerator.downloadReceipt(enhancedSale, filename, options)
      
      console.log('✅ Enhanced PDF receipt generated successfully')
    } catch (error) {
      console.error('❌ Failed to generate enhanced PDF receipt:', error)
      // Fallback to regular receipt generation
      ReceiptGenerator.downloadReceipt(sale, filename, options)
    }
  }

  /**
   * Generate enhanced HTML receipt with product details
   * @param {Object} sale - Sale data
   * @param {string} filename - HTML filename
   * @param {Object} options - Receipt options
   */
  static async generateEnhancedHTMLReceipt(sale, filename, options = {}) {
    try {
      console.log('🔍 Fetching product details for HTML receipt...')

      // Enhance items with product details
      const enhancedSale = {
        ...sale,
        items: await this.enhanceItemsWithProductDetails(sale.items || [])
      }

      // Generate HTML with enhanced data
      HTMLReceiptGenerator.downloadHTML(enhancedSale, filename, options)
      
      console.log('✅ Enhanced HTML receipt generated successfully')
    } catch (error) {
      console.error('❌ Failed to generate enhanced HTML receipt:', error)
      // Fallback to regular receipt generation
      HTMLReceiptGenerator.downloadHTML(sale, filename, options)
    }
  }

  /**
   * Print enhanced receipt with product details
   * @param {Object} sale - Sale data
   * @param {Object} options - Receipt options
   */
  static async printEnhancedReceipt(sale, options = {}) {
    try {
      console.log('🔍 Fetching product details for print receipt...')

      // Enhance items with product details
      const enhancedSale = {
        ...sale,
        items: await this.enhanceItemsWithProductDetails(sale.items || [])
      }

      // Print with enhanced data
      HTMLReceiptGenerator.printReceipt(enhancedSale, options)
      
      console.log('✅ Enhanced print receipt generated successfully')
    } catch (error) {
      console.error('❌ Failed to generate enhanced print receipt:', error)
      // Fallback to regular receipt generation
      HTMLReceiptGenerator.printReceipt(sale, options)
    }
  }

  /**
   * Clear product cache (useful for memory management)
   */
  static clearCache() {
    this.productCache.clear()
    console.log('🧹 Product cache cleared')
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache stats
   */
  static getCacheStats() {
    return {
      size: this.productCache.size,
      keys: Array.from(this.productCache.keys())
    }
  }
}